/**
 * The month ring's behaviour and words (face 2 of the 0, zeroMonth.ts), as one hook.
 *
 * ONE copy, read by today's ring (./ZeroMonth.tsx, on /pricing) and by the pricing page's rebuild in the new design, which
 * may not import a styled-components module (design-system §7b: behaviour is lifted into a hook, never copied). Lifted on
 * 2026-09-30; the behaviour is unchanged:
 *
 * - it is a display you read, not a second calculator: select an arc and it says what that slice is, how many, at what
 *   published rate, and what share of the month it takes;
 * - it steps through the five arcs every 3.2 s until the visitor touches it, and never under reduced motion;
 * - it is ONE slider (arrow keys, Home, End), spread onto whichever drawing is on screen (the ring, or the bars on a
 *   phone), so there is never a second slider for one month.
 *
 * The geometry is here too, so both drawings put every arc in the same place.
 */
import React from 'react';
import { WU_LABEL } from '../../config/workUnits';
import { MONTH_TOTAL_WU, monthArcs, typicalCounts, type MonthArc } from './zeroMonth';

export const MONTH_RING_STEP_MS = 3200;

/** The ring's drawing, in its 260 × 260 box. */
export const RING = { R: 104, WIDTH: 26, CX: 130, CY: 130, SIZE: 260 } as const;

/**
 * The five arcs step through the gold's own opacity rather than inventing colours; the SELECTED arc is the only one at
 * full strength, which is what carries the reading.
 */
export const ARC_TONES = [0.9, 0.72, 0.54, 0.38, 0.24];

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/** The SVG path of one arc of the ring, 12 o'clock up, clockwise. */
export function arcPath(startDeg: number, endDeg: number): string {
  const sweep = Math.min(359.99, Math.max(0, endDeg - startDeg));
  const a = polar(RING.CX, RING.CY, RING.R, startDeg);
  const b = polar(RING.CX, RING.CY, RING.R, startDeg + sweep);
  return `M ${a.x} ${a.y} A ${RING.R} ${RING.R} 0 ${sweep > 180 ? 1 : 0} 1 ${b.x} ${b.y}`;
}

const grouped = (n: number) => n.toLocaleString('en-US');

/** The ring's words, for the arc that is selected. */
export function monthRingWords(current: MonthArc, pct: number) {
  return {
    /** The total in the middle of the ring, and the line under it. */
    total: grouped(MONTH_TOTAL_WU),
    totalUnit: `${WU_LABEL} a month`,
    /** One bar's figure. */
    barFigure: (arc: MonthArc) => `${arc.wu} ${WU_LABEL}`,
    /** The selected slice: its count and what it is. */
    slice: `${grouped(current.count)} ${current.item}`,
    /** "3 pr0 each — the published rate. that is" · "360 pr0" (strong) · ", 16% of the month." */
    detailLead: `${current.wuEach} ${WU_LABEL} each — the published rate. that is `,
    detailFigure: `${current.wu} ${WU_LABEL}`,
    detailTail: `, ${pct}% of the month.`,
    whole: `the whole circle is ${grouped(MONTH_TOTAL_WU)} ${WU_LABEL} — one Prest0n’s month. these five are exactly that.`,
    hint: 'tap a slice, or use the arrow keys',
    live: `${current.item}. ${current.wu} ${WU_LABEL}, ${pct} percent of the month.`,
  };
}

export interface MonthRingOptions {
  autoPlay?: boolean;
}

export function useMonthRing({ autoPlay = true }: MonthRingOptions = {}) {
  const arcs = React.useMemo<MonthArc[]>(() => monthArcs(typicalCounts()), []);
  const [index, setIndex] = React.useState(0);
  const [touched, setTouched] = React.useState(false);

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const playing = autoPlay && !touched && !reduceMotion;

  React.useEffect(() => {
    if (!playing) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % arcs.length), MONTH_RING_STEP_MS);
    return () => window.clearInterval(id);
  }, [playing, arcs.length]);

  /** Select an arc (by a click or a key): the ring stops stepping on its own. */
  const take = React.useCallback(
    (next: number) => {
      setTouched(true);
      setIndex(((next % arcs.length) + arcs.length) % arcs.length);
    },
    [arcs.length],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); take(index + 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); take(index - 1); }
    else if (k === 'Home') { e.preventDefault(); take(0); }
    else if (k === 'End') { e.preventDefault(); take(arcs.length - 1); }
  };

  const current = arcs[Math.min(index, arcs.length - 1)];
  const pct = Math.round(current.share * 100);

  /** The one slider's attributes, spread onto the drawing that is on screen. */
  const sliderProps = {
    role: 'slider' as const,
    tabIndex: 0,
    'aria-label': `what one Prest0n's month of ${grouped(MONTH_TOTAL_WU)} ${WU_LABEL} goes on — use the arrow keys`,
    'aria-valuemin': 0,
    'aria-valuemax': arcs.length - 1,
    'aria-valuenow': index,
    'aria-valuetext': `${current.item} — ${current.wu} ${WU_LABEL}, ${pct}% of the month`,
    onKeyDown,
  };

  return { arcs, index, current, pct, take, sliderProps, words: monthRingWords(current, pct) };
}
